import { useState } from 'react';
import { usePlanNoble, useRemoveNobleAssignment } from '../api/queries';
import type { AgPlanDetail, NobleCandidate, NobleCandidatesResult } from '../api/types';
import { useToast } from '../components/Toast';
import { formatDateTime, formatDuration } from './mapUtils';

export interface IncRow {
  id: number;
  sourceX: number;
  sourceY: number;
  arrivalMs: number;
  unit: string;
  typeLabel: string;
  color: string;
}

interface Props {
  welt: string;
  planId: number;
  detail: AgPlanDetail;
  village: { x: number; y: number };
  incs: IncRow[];
  selectedInc: number | null;
  onSelectInc: (id: number | null) => void;
  gapMs: number;
  onGapChange: (gapMs: number) => void;
  candidates: NobleCandidatesResult | null;
  candidatesLoading: boolean;
  candidatesError: string | null;
}

const REASONS: Record<Exclude<NobleCandidate['status'], 'ok'>, string> = {
  past: 'Laufzeit zu lang – Abschickzeit läge in der Vergangenheit',
  beforeInc: 'würde vor der Off abgeschickt',
};

/**
 * AG-Planung für das gewählte Zieldorf: Incs (Offs) auf das Dorf, nach Klick
 * auf einen Inc die möglichen AG-Dörfer (Laufzeit + Abschickzeit nach der Off),
 * Anzahl wählen und einplanen; bereits geplante AGs auf dieses Dorf.
 */
export function NoblePlanningPanel({
  welt,
  planId,
  detail,
  village,
  incs,
  selectedInc,
  onSelectInc,
  gapMs,
  onGapChange,
  candidates,
  candidatesLoading,
  candidatesError,
}: Props) {
  const toast = useToast();
  const plan = usePlanNoble(welt);
  const remove = useRemoveNobleAssignment(welt);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [showImpossible, setShowImpossible] = useState(false);

  const plannedHere = detail.plannedNobles.filter((p) => p[3] === village.x && p[4] === village.y);
  const possible = candidates?.candidates.filter((c) => c.status === 'ok') ?? [];
  const impossible = candidates?.candidates.filter((c) => c.status !== 'ok') ?? [];

  const countFor = (c: NobleCandidate) => Math.min(c.available, Math.max(1, counts[`${c.x}|${c.y}`] ?? 1));

  const handlePlan = (c: NobleCandidate) => {
    if (selectedInc === null) return;
    plan.mutate(
      { planId, commandId: selectedInc, sourceX: c.x, sourceY: c.y, count: countFor(c), gapMs },
      {
        onSuccess: () => toast.push('success', `${countFor(c)} AG(s) aus ${c.name} eingeplant.`),
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  if (incs.length === 0 && plannedHere.length === 0) return null;

  return (
    <div className="ag-side-section ag-noble-plan">
      {incs.length > 0 && (
        <>
          <h3>Incs auf dieses Dorf</h3>
          <ul className="ag-incs">
            {incs.map((inc) => (
              <li key={inc.id}>
                <button
                  type="button"
                  className={inc.id === selectedInc ? 'ag-inc ag-inc-active' : 'ag-inc'}
                  onClick={() => onSelectInc(inc.id === selectedInc ? null : inc.id)}
                  aria-pressed={inc.id === selectedInc}
                >
                  <i style={{ background: inc.color }} />
                  <span className="ag-inc-main">
                    {inc.typeLabel} · {inc.unit || '?'} von {inc.sourceX}|{inc.sourceY}
                  </span>
                  <span className="muted">Ankunft {formatDateTime(inc.arrivalMs, true)}</span>
                </button>
              </li>
            ))}
          </ul>
          {selectedInc === null && <p className="muted">Inc anklicken, um mögliche AGs dahinter zu berechnen.</p>}
        </>
      )}

      {selectedInc !== null && (
        <div className="ag-candidates">
          <h3>AGs hinter diesem Inc</h3>
          {candidatesLoading && <p className="muted">Wird berechnet …</p>}
          {candidatesError && <div className="flash flash-error">{candidatesError}</div>}
          {candidates && (
            <>
              <p className="muted">
                AG-Ankunft: <strong>{formatDateTime(candidates.arrivalMs, true)}</strong> (Abstand
                <input
                  type="number"
                  min={1}
                  max={1000}
                  className="ag-gap-input"
                  value={gapMs}
                  onChange={(event) => onGapChange(Math.max(1, Math.min(1000, Math.round(Number(event.target.value)) || 1)))}
                  aria-label="Abstand hinter der Off in Millisekunden"
                />
                ms)
                {candidates.anchor.launchMs !== null && (
                  <>
                    <br />
                    Abschickzeit der Off: {formatDateTime(candidates.anchor.launchMs)}
                  </>
                )}
              </p>
              {candidates.laterCommands > 0 && (
                <div className="flash flash-error">
                  Achtung: {candidates.laterCommands} weitere Befehl(e) kommen NACH diesem Inc an – die AG käme nicht hinter die letzte Off.
                </div>
              )}

              {possible.length === 0 && !candidatesLoading && <p className="muted">Kein Dorf mit ungeplanten AGs kann diese Sekunde noch erreichen.</p>}
              <ul className="ag-cand-list">
                {possible.map((c) => (
                  <li key={`${c.x}|${c.y}`}>
                    <div className="ag-cand-head">
                      <strong>
                        {c.name} ({c.x}|{c.y})
                      </strong>
                      <span className="muted">{c.owner ?? '–'}</span>
                    </div>
                    <div className="muted ag-cand-meta">
                      {c.distance} Felder · Laufzeit {formatDuration(c.travelSeconds)} · abschicken {formatDateTime(c.launchMs, true)}
                      {c.outsideWindow && <span className="ag-warn"> · ⚠ außerhalb der Abschickzeit des Spielers</span>}
                    </div>
                    <div className="ag-cand-action">
                      <span className="muted">{c.available} ungeplant</span>
                      <input
                        type="number"
                        min={1}
                        max={c.available}
                        value={countFor(c)}
                        onChange={(event) => setCounts((previous) => ({ ...previous, [`${c.x}|${c.y}`]: Math.round(Number(event.target.value)) || 1 }))}
                        aria-label={`Anzahl AGs aus ${c.name}`}
                      />
                      <button type="button" className="btn btn-sm btn-primary" disabled={plan.isPending} onClick={() => handlePlan(c)}>
                        Einplanen
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              {impossible.length > 0 && (
                <>
                  <button type="button" className="ag-link-button" onClick={() => setShowImpossible((open) => !open)}>
                    {showImpossible ? 'Nicht mögliche ausblenden' : `Nicht mögliche anzeigen (${impossible.length})`}
                  </button>
                  {showImpossible && (
                    <ul className="ag-cand-list ag-cand-impossible">
                      {impossible.map((c) => (
                        <li key={`${c.x}|${c.y}`}>
                          <strong>
                            {c.name} ({c.x}|{c.y})
                          </strong>{' '}
                          <span className="muted">
                            · {c.available} ungeplant · {REASONS[c.status as Exclude<NobleCandidate['status'], 'ok'>]}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {plannedHere.length > 0 && (
        <>
          <h3>Geplante AGs auf dieses Dorf</h3>
          <ul className="ag-planned">
            {plannedHere.map(([id, sx, sy, , , count, arrivalMs, launchMs]) => (
              <li key={id}>
                <span>
                  <strong>{count} AG</strong> von {sx}|{sy}
                  <br />
                  <span className="muted">
                    Ankunft {formatDateTime(arrivalMs, true)} · abschicken {formatDateTime(launchMs, true)}
                  </span>
                </span>
                <button
                  type="button"
                  className="ag-link-button"
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(
                      { planId, assignmentId: id },
                      { onSuccess: () => toast.push('success', 'Einplanung entfernt.'), onError: (error) => toast.push('error', error.message) },
                    )
                  }
                >
                  entfernen
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
